import {all, call, put, takeLatest, takeLeading} from 'redux-saga/effects';
import {actions, ActionType} from './action';
import {
  getGiftEvents,
  getGiftRedDotStatus,
  getGiftTickets,
  getReceivedTicket,
} from './apiGet';
import {pAlert} from '../../common/component/PAlert';
import SingleText from '../../common/component/SingleText';
import Text from '../../common/component/Text';
import {GIFT_LAST_DATE_TIME_KEY} from '../../common/constant';
import {Page} from '../../common/constant/page';
import {selectT} from '../../common/i18n';
import {getClientStore} from '../../common/store';
import {ButtonSet} from '../../common/type/alert';
import callApi from '../../common/util/api';
import {makeFetchSaga} from '../../common/util/fetch';
import {routeTo} from '../../common/util/PageRouter';
import {getLocalStorageString} from '../../common/util/storage';
import {sendEvent, sendViewImp} from '../../common/util/tracking';

function* fetchGifts(action: ReturnType<typeof actions.fetchGifts>) {
  const {tab} = action.payload;
  const {success, data} = yield call(callApi, {
    url:
      tab === 'events'
        ? '/gift/v2/views/giftbox/events'
        : '/gift/v1/views/giftbox/gifts',
  });

  if (success && data) {
    yield put(
      actions.setValue(
        tab === 'events' ? 'event' : 'ticket',
        tab === 'events' ? getGiftEvents(data) : getGiftTickets(data)
      )
    );
    yield put(
      actions.setValue(tab === 'events' ? 'ticket' : 'event', undefined)
    );
    yield put(actions.setValue('needRefresh', false));
  }
}

function* fetchReceiveTicket(
  action: ReturnType<typeof actions.fetchReceiveTicket>
) {
  const {ticketId} = action.payload;
  const {success, data} = yield call(callApi, {
    method: 'POST',
    url: `/gift/v1/gift-tickets/${ticketId}/receive`,
    processApiErrorCallback: error => {
      const store = getClientStore();
      switch (error.errorType) {
        case 'GIFTBOX_TICKET_ALREADY_RECEIVED':
          store?.dispatch(actions.setReceivedTicket(ticketId));
          break;
        case 'GIFTBOX_TICKET_NOT_AVAILABLE_PERIOD':
          store?.dispatch(actions.removeExpiredTicket(ticketId));
          break;
      }
      return false;
    },
  });

  if (success && data) {
    const t = yield* selectT();
    const {content, ticketCount, isExpired} = getReceivedTicket(data);
    isExpired && pAlert({title: t('gift_ticket_fail_expired_popup')});
    if (!isExpired) {
      sendViewImp({
        section: 'giftbox',
        page: 'giftbox_ticket',
        customProps: {
          content_id: content.id,
          event_layer: 'popup',
          popup_type: 'giftbox_receive_ticket',
        },
        viewImpContents: {name: 'popup'},
      });
      yield put(actions.setReceivedTicket(ticketId));
      pAlert({
        isButtonVertical: true,
        title: (
          <div className="text-center">
            <SingleText className="s17-medium-white light:text-black">
              {content.title}
            </SingleText>
            <Text className="s17-medium-white light:text-black">
              {t('giftbox_tab_received_popup', {count: ticketCount})}
            </Text>
          </div>
        ),
        desc: t('giftbox_tab_received_valid_within_popup'),
        buttonSet: ButtonSet.CancelAndConfirm,
        labelConfirm: t('luckydraw_ticket_button'),
        labelClose: t('common_ok'),
      }).then(({okClicked, isDimmedClicked}) => {
        sendEvent({
          section: 'giftbox',
          page: 'giftbox_ticket',
          actionName: okClicked ? 'content' : 'popup_ok',
          action: 'click',
          customProps: {
            content_id: content.id,
            event_layer: isDimmedClicked ? 'dimmed' : 'popup',
            popup_type: 'giftbox_receive_ticket',
          },
        });

        okClicked &&
          routeTo({page: Page.Content, id: content.id, sid: content.sid});
      });
    }
  }
}

function* fetchGiftRedDotStatus() {
  const lastDateTime = getLocalStorageString({
    key: GIFT_LAST_DATE_TIME_KEY,
  });
  const {success, data} = yield call(callApi, {
    url: 'gift/v1/last-read-time',
    params: {
      eventsLastReadDateTime: lastDateTime,
      giftsLastReadDateTime: lastDateTime,
    },
  });

  if (success) {
    yield put(actions.setValue('giftRedDotStatus', getGiftRedDotStatus(data)));
  }
}

export default function* saga() {
  yield all([
    takeLatest(
      ActionType.FetchGifts,
      makeFetchSaga({
        fetchSaga: fetchGifts,
        canCache: false,
      })
    ),
    takeLeading(
      ActionType.FetchReceiveTicket,
      makeFetchSaga({
        fetchSaga: fetchReceiveTicket,
        canCache: false,
      })
    ),
    takeLeading(
      ActionType.FetchGiftRedDotStatus,
      makeFetchSaga({
        fetchSaga: fetchGiftRedDotStatus,
        canCache: false,
      })
    ),
  ]);
}
